import { Controller } from "@hotwired/stimulus"

// Skills are chips in the UI but stay a comma-joined string in storage
// (sections[key=skills].content — unchanged by this task; autosave still
// picks it up through the same [data-resume-section-content] hook the old
// textarea carried, see resume_editor_controller#serialize). Appending to
// that string is what let "Add missing keywords" accumulate duplicates;
// reading the current set through a Set on every write fixes that at the
// source.
export default class extends Controller {
  static targets = ["value", "list", "addSlot", "addButton", "input"]

  connect() {
    this.closeInput()
  }

  // ---- storage <-> chip list ---------------------------------------------
  read() {
    return this.valueTarget.value.split(",").map((s) => s.trim()).filter(Boolean)
  }

  write(list) {
    const seen = new Set()
    const deduped = list.filter((skill) => {
      const key = skill.toLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    this.valueTarget.value = deduped.join(", ")
    this.valueTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.renderChips(deduped)
  }

  // Reconciles the DOM to `list` instead of destroying and rebuilding every
  // <li>. A full rebuild would tear down the chip under the pointer when
  // write() runs from commit()'s blur handler — mousedown -> blur -> click
  // is the native order for clicking a chip's remove button while the
  // add-input has focus, so blur's rebuild would detach the element the
  // click is about to land on, and the browser drops that click.
  //
  // Leaving an unrelated chip's <li> untouched isn't enough on its own:
  // Node.before()/insertBefore() detach-then-reinsert a node even when it
  // is called with the node's current, already-correct position, and that
  // momentary detach is all it takes to break the browser's native
  // mousedown->click target tracking, same as a full rebuild would. So walk
  // back-to-front and only touch the DOM when a chip is actually new or
  // actually out of place — an unaffected chip gets zero DOM operations.
  renderChips(list) {
    const bySkill = new Map(
      Array.from(this.listTarget.querySelectorAll("[data-skill-chip]")).map((chip) => [
        chip.getAttribute("data-skill-chip"),
        chip
      ])
    )
    const wanted = new Set(list)

    bySkill.forEach((chip, skill) => {
      if (!wanted.has(skill)) chip.remove()
    })

    let reference = this.addSlotTarget
    for (let i = list.length - 1; i >= 0; i--) {
      const chip = bySkill.get(list[i]) || this.buildChip(list[i])
      if (chip.nextElementSibling !== reference) this.listTarget.insertBefore(chip, reference)
      reference = chip
    }
  }

  buildChip(skill) {
    const li = document.createElement("li")
    li.className = "rbf-skill"
    li.setAttribute("data-skill-chip", skill)

    const label = document.createElement("span")
    label.textContent = skill

    const remove = document.createElement("button")
    remove.type = "button"
    remove.setAttribute("data-action", "skill-chips#remove")
    remove.setAttribute("aria-label", `Remove ${skill}`)
    const icon = document.createElement("span")
    icon.className = "material-symbols-outlined"
    icon.setAttribute("aria-hidden", "true")
    icon.textContent = "close"
    remove.appendChild(icon)

    li.append(label, remove)
    return li
  }

  remove(event) {
    const chip = event.target.closest("[data-skill-chip]")
    if (!chip) return
    const name = chip.getAttribute("data-skill-chip")
    this.write(this.read().filter((s) => s !== name))
  }

  // ---- inline "Add skill" input, replacing the earlier window.prompt() --
  // placeholder: a browser prompt can't be styled or reached predictably by
  // a test, and it's jarring in an otherwise-polished editor. Commits on
  // both Enter and blur; the `hidden`-class guard in commit() makes the two
  // idempotent when Enter's own closeInput() blurs the field a moment later.
  openInput() {
    this.addButtonTarget.classList.add("hidden")
    this.inputTarget.classList.remove("hidden")
    this.inputTarget.value = ""
    this.inputTarget.focus()
  }

  closeInput() {
    this.inputTarget.classList.add("hidden")
    this.addButtonTarget.classList.remove("hidden")
  }

  commit() {
    if (this.inputTarget.classList.contains("hidden")) return
    const name = this.inputTarget.value.trim()
    this.closeInput()
    if (name) this.write([...this.read(), name])
  }

  keydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.commit()
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.inputTarget.value = ""
      this.closeInput()
    }
  }
}
